import {Alert, Skeleton} from "@mui/material";
import React, {ReactNode, Suspense, useMemo} from "react";
import {Await, useAsyncError} from "react-router-dom";
import {apiErrorMessage} from "../../util/ApiErrorMessage";
import {RemoteValue} from "../../util/RemoteValue";

function LoadingErrorFallback({defaultErrorMessage}: {
    defaultErrorMessage: string,
}) {
    const error = useAsyncError();
    const errorMessages = apiErrorMessage(error) || defaultErrorMessage;

    return (
        <Alert severity="error" sx={{mt: 2, mb: 2}}>
            {errorMessages}
        </Alert>
    );
}

function LoadingEmptyMessage({emptyMessage}: { emptyMessage: string }) {
    return (
        <Alert severity="info" sx={{mt: 2, mb: 2}}>
            {emptyMessage}
        </Alert>
    );
}

export function LoadingData<Entities extends { length: number }, TotalEntities extends { length: number } = Entities>(
    {
        value,
        totalValue,
        defaultErrorMessage,
        emptyMessage,
        noMatchMessage,
        children
    }: {
        value: RemoteValue<Entities>,
        totalValue?: RemoteValue<TotalEntities>,
        defaultErrorMessage: string,
        emptyMessage: string,
        noMatchMessage?: string,
        children: (resolved: Entities) => ReactNode,
    }
) {
    const rv = useMemo(
        () => totalValue
            ? RemoteValue.combine({totalValue, value})
                .map(({totalValue: totalValueResolved, value: valueResolved}) => ({
                    emptyMessage: totalValueResolved.length === 0
                        ? emptyMessage
                        : valueResolved.length === 0
                            ? noMatchMessage || 'Nothing matches the current filters. Try adjusting your filter criteria.'
                            : null,
                    value: valueResolved,
                }))
            : value.map(valueResolved => ({
                emptyMessage: valueResolved.length === 0 ? emptyMessage : null,
                value: valueResolved,
            })),
        [value, totalValue, emptyMessage, noMatchMessage],
    );

    return (
        <Loading
            loading={<Skeleton width="100%" height="90vh" variant="rounded"/>}
            errorElement={<LoadingErrorFallback defaultErrorMessage={defaultErrorMessage}/>}
            value={rv}
        >{({emptyMessage, value: entities}) =>
            emptyMessage ? (
                <LoadingEmptyMessage emptyMessage={emptyMessage}/>
            ) : (
                children(entities)
            )
        }</Loading>
    );
}

export function Loading<Value>({value, loading, skeletonValue, errorElement, children}: {
    value: RemoteValue<Value>,
    loading?: ReactNode,
    skeletonValue?: Value,
    errorElement?: ReactNode,
    children: (value: Value) => ReactNode,
}) {
    return (
        <Suspense fallback={loading || (skeletonValue && <Skeleton variant="rounded">{children(skeletonValue)}</Skeleton>)}>
            <Await resolve={value.value} errorElement={errorElement} children={children}/>
        </Suspense>
    );
}